import React, { useState } from 'react';
import './VestingInfo.css';
import DelegationHelper from './DelegationHelper';

function VestingInfo() {
  const [openSections, setOpenSections] = useState({
    claim: true,
    abininja: false,
    explorer: false,
    arbiscan: false,
    l1l2: false,
    basics: false,
    elements: false,
    examples: false,
  });

  const toggle = (key) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="notion-page">
      {/* ── Cover ── */}
      <div className="notion-cover">
        <div className="notion-cover-gradient" />
      </div>

      {/* ── Page Icon + Title ── */}
      <div className="notion-page-header">
        <span className="notion-page-icon">📑</span>
        <h1 className="notion-title">Graph Vesting Contracts</h1>
        <p className="notion-subtitle">
          Everything you need to understand, check, and claim your vested GRT tokens.
        </p>
        <div className="notion-meta">
          <span className="notion-meta-item">🔗 Arbitrum One</span>
          <span className="notion-meta-divider">·</span>
          <span className="notion-meta-item">📦 GraphTokenLockWallet</span>
          <span className="notion-meta-divider">·</span>
          <span className="notion-meta-item">🛠️ ABI Ninja + Graph Explorer</span>
        </div>
      </div>

      {/* ── Table of Contents ── */}
      <nav className="notion-toc">
        <div className="notion-toc-label">On this page</div>
        <button className="notion-toc-item" onClick={() => scrollTo('section-claim')}>🔓 How to Claim Vested GRT</button>
        <button className="notion-toc-item notion-toc-sub" onClick={() => scrollTo('section-abininja')}>⚡ ABI Ninja (Recommended)</button>
        <button className="notion-toc-item notion-toc-sub" onClick={() => scrollTo('section-explorer')}>🌐 Graph Explorer</button>
        <button className="notion-toc-item notion-toc-sub" onClick={() => scrollTo('section-arbiscan')}>🔍 Arbiscan (Manual)</button>
        <button className="notion-toc-item" onClick={() => scrollTo('section-delegation')}>🧊 Undelegate &amp; claim (ETH + Arbitrum)</button>
        <button className="notion-toc-item" onClick={() => scrollTo('section-l1l2')}>⚠️ L1→L2 Migration</button>
        <button className="notion-toc-item" onClick={() => scrollTo('section-basics')}>📘 How Vesting Works</button>
        <button className="notion-toc-item" onClick={() => scrollTo('section-elements')}>🧩 Key Contract Elements</button>
        <button className="notion-toc-item" onClick={() => scrollTo('section-examples')}>💡 Example Scenarios</button>
      </nav>

      <div className="notion-divider" />

      {/* ════════════════════════════════════════════════
          SECTION — Claim Your GRT
         ════════════════════════════════════════════════ */}
      <section id="section-claim" className="notion-section">
        <h2 className="notion-h2">🔓 How to Claim Your Vested GRT</h2>
        <p className="notion-text">
          Once tokens vest inside your <code className="notion-code">GraphTokenLockWallet</code>, you need to manually release them.
          Below are three tools — pick whichever you're most comfortable with.
        </p>

        {/* ── Tool Comparison Cards ── */}
        <div className="notion-tool-grid">
          <div className="notion-tool-card notion-tool-card--primary">
            <div className="notion-tool-card-badge">⭐ Recommended</div>
            <div className="notion-tool-card-icon">⚡</div>
            <h4>ABI Ninja</h4>
            <p>Cleanest UI for reading &amp; writing contract functions. Built by BuidlGuidl.</p>
            <a href="https://abi.ninja" target="_blank" rel="noopener noreferrer" className="notion-tool-link">abi.ninja →</a>
          </div>
          <div className="notion-tool-card">
            <div className="notion-tool-card-icon">🌐</div>
            <h4>Graph Explorer</h4>
            <p>Official UI with built-in vesting panel. Connect wallet, click release.</p>
            <a href="https://thegraph.com/explorer" target="_blank" rel="noopener noreferrer" className="notion-tool-link">thegraph.com/explorer →</a>
          </div>
          <div className="notion-tool-card">
            <div className="notion-tool-card-icon">🔍</div>
            <h4>Arbiscan</h4>
            <p>Block explorer fallback. Read/Write as Proxy on the contract page.</p>
            <a href="https://arbiscan.io" target="_blank" rel="noopener noreferrer" className="notion-tool-link">arbiscan.io →</a>
          </div>
        </div>
      </section>

      {/* ── ABI Ninja (Recommended) ── */}
      <section id="section-abininja" className="notion-section">
        <button className={`notion-toggle ${openSections.abininja ? 'open' : ''}`} onClick={() => toggle('abininja')}>
          <span className="notion-toggle-icon" />
          <span className="notion-toggle-text">⚡ Claim with ABI Ninja <span className="notion-pill notion-pill--primary">Recommended</span></span>
        </button>

        <div className={`notion-toggle-body ${openSections.abininja ? 'open' : ''}`}>
          <div className="notion-callout notion-callout--info">
            <span className="notion-callout-icon">💡</span>
            <div>
              <strong>ABI Ninja</strong> is an open-source tool by <a href="https://buidlguidl.com" target="_blank" rel="noopener noreferrer">BuidlGuidl</a> that gives you a clean UI to interact with any smart contract on any EVM chain — no need to paste ABIs or navigate block explorer tabs.
            </div>
          </div>

          <div className="notion-steps">
            <div className="notion-step">
              <div className="notion-step-num">1</div>
              <div className="notion-step-body">
                <h4>Open Your Contract in ABI Ninja</h4>
                <p>Go to <a href="https://abi.ninja" target="_blank" rel="noopener noreferrer" className="notion-link">abi.ninja</a>, paste your vesting contract address, and select <strong>Arbitrum One</strong> (chain 42161).</p>
                <div className="notion-callout notion-callout--tip">
                  <span className="notion-callout-icon">⚡</span>
                  <div>Or use a direct link: <code className="notion-code">abi.ninja/YOUR_CONTRACT_ADDRESS/42161</code></div>
                </div>
              </div>
            </div>

            <div className="notion-step">
              <div className="notion-step-num">2</div>
              <div className="notion-step-body">
                <h4>Read Your Vesting Status</h4>
                <p>ABI Ninja auto-loads the contract's ABI. Use these <strong>Read</strong> functions:</p>
                <div className="notion-fn-grid">
                  <div className="notion-fn-item">
                    <code>releasableAmount</code>
                    <span>GRT you can withdraw right now</span>
                  </div>
                  <div className="notion-fn-item">
                    <code>vestedAmount</code>
                    <span>Total vested so far</span>
                  </div>
                  <div className="notion-fn-item">
                    <code>currentBalance</code>
                    <span>GRT remaining in contract</span>
                  </div>
                  <div className="notion-fn-item">
                    <code>beneficiary</code>
                    <span>Wallet that can claim</span>
                  </div>
                  <div className="notion-fn-item">
                    <code>endTime</code>
                    <span>Vesting end (Unix timestamp)</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="notion-step">
              <div className="notion-step-num">3</div>
              <div className="notion-step-body">
                <h4>Connect &amp; Release</h4>
                <p>Click <strong>"Connect Wallet"</strong> in ABI Ninja, then scroll to the <code className="notion-code">release</code> write function and click <strong>"Send"</strong>. Confirm in your wallet.</p>
                <div className="notion-checklist">
                  <div className="notion-check-item"><span className="notion-check">✓</span> Wallet on <strong>Arbitrum One</strong></div>
                  <div className="notion-check-item"><span className="notion-check">✓</span> Connected with <strong>beneficiary address</strong></div>
                  <div className="notion-check-item"><span className="notion-check">✓</span> Small amount of <strong>ETH for gas</strong> (~$0.10)</div>
                </div>
              </div>
            </div>
          </div>

          <div className="notion-callout notion-callout--success">
            <span className="notion-callout-icon">✅</span>
            <div>Done! Your vested GRT will be sent to your beneficiary wallet on Arbitrum One.</div>
          </div>
        </div>
      </section>

      {/* ── Graph Explorer ── */}
      <section id="section-explorer" className="notion-section">
        <button className={`notion-toggle ${openSections.explorer ? 'open' : ''}`} onClick={() => toggle('explorer')}>
          <span className="notion-toggle-icon" />
          <span className="notion-toggle-text">🌐 Claim with Graph Explorer</span>
        </button>

        <div className={`notion-toggle-body ${openSections.explorer ? 'open' : ''}`}>
          <p className="notion-text">The Graph Explorer has a built-in vesting panel — no contract interaction needed.</p>

          <div className="notion-steps">
            <div className="notion-step">
              <div className="notion-step-num">1</div>
              <div className="notion-step-body">
                <h4>Open Graph Explorer</h4>
                <p>Go to <a href="https://thegraph.com/explorer" target="_blank" rel="noopener noreferrer" className="notion-link">thegraph.com/explorer</a></p>
              </div>
            </div>
            <div className="notion-step">
              <div className="notion-step-num">2</div>
              <div className="notion-step-body">
                <h4>Connect Your Beneficiary Wallet</h4>
                <p>Switch to <strong>Arbitrum One</strong> and connect the wallet linked to your vesting contract.</p>
              </div>
            </div>
            <div className="notion-step">
              <div className="notion-step-num">3</div>
              <div className="notion-step-body">
                <h4>Navigate to Your Profile</h4>
                <p>The Explorer should auto-detect your vesting contract and show a vesting panel.</p>
              </div>
            </div>
            <div className="notion-step">
              <div className="notion-step-num">4</div>
              <div className="notion-step-body">
                <h4>Click Release / Withdraw</h4>
                <p>You'll see your vested balance and a <strong>"Release"</strong> or <strong>"Withdraw"</strong> button. Click it and confirm.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Arbiscan Fallback ── */}
      <section id="section-arbiscan" className="notion-section">
        <button className={`notion-toggle ${openSections.arbiscan ? 'open' : ''}`} onClick={() => toggle('arbiscan')}>
          <span className="notion-toggle-icon" />
          <span className="notion-toggle-text">🔍 Claim via Arbiscan <span className="notion-pill notion-pill--muted">Manual fallback</span></span>
        </button>

        <div className={`notion-toggle-body ${openSections.arbiscan ? 'open' : ''}`}>
          <p className="notion-text">If neither ABI Ninja nor Graph Explorer works, interact with the contract directly on Arbiscan.</p>

          <div className="notion-steps">
            <div className="notion-step">
              <div className="notion-step-num">1</div>
              <div className="notion-step-body">
                <h4>Open Your Contract on Arbiscan</h4>
                <p>Go to <code className="notion-code">arbiscan.io/address/YOUR_CONTRACT</code> → <strong>Contract</strong> tab → <strong>Read as Proxy</strong>.</p>
              </div>
            </div>
            <div className="notion-step">
              <div className="notion-step-num">2</div>
              <div className="notion-step-body">
                <h4>Check Vesting Status</h4>
                <p>Call <code className="notion-code">releasableAmount</code> to see how much GRT is ready. If it returns <code className="notion-code">0</code>, tokens haven't vested yet.</p>
              </div>
            </div>
            <div className="notion-step">
              <div className="notion-step-num">3</div>
              <div className="notion-step-body">
                <h4>Switch to "Write as Proxy"</h4>
                <p>Click <strong>"Connect to Web3"</strong>, then find the <code className="notion-code">release</code> function and click <strong>"Write"</strong>.</p>
              </div>
            </div>
            <div className="notion-step">
              <div className="notion-step-num">4</div>
              <div className="notion-step-body">
                <h4>Confirm in Wallet</h4>
                <p>Approve the transaction. Vested GRT goes straight to your beneficiary address on Arbitrum.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="notion-divider" />

      <DelegationHelper />

      <div className="notion-divider" />

      {/* ════════════════════════════════════════════════
          SECTION — L1→L2 Migration Warning
         ════════════════════════════════════════════════ */}
      <section id="section-l1l2" className="notion-section">
        <h2 className="notion-h2">⚠️ L1→L2 Migrated Vesting Contracts</h2>

        <div className="notion-callout notion-callout--warning">
          <span className="notion-callout-icon">⚠️</span>
          <div>
            If your Arbitrum vesting wallet was created from an L1 (Ethereum) contract, you <strong>cannot release on L2</strong> until the full vesting schedule ends. You must bridge tokens back to L1 first.
          </div>
        </div>

        <div className="notion-callout notion-callout--tip">
          <span className="notion-callout-icon">ℹ️</span>
          <div>
            <strong>First, free any delegated GRT on Arbitrum.</strong> The bridge can only move GRT that is sitting in the L2 lock wallet.
            If the L2 contract is still delegated, run steps 1–2 of the <button type="button" className="notion-link" style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }} onClick={() => scrollTo('section-delegation')}>Undelegate &amp; claim helper</button> (choose Arbitrum) and wait ~28 days first.
            After the tokens reach L1, finish with <code className="notion-code">release</code> and, if anything is left above the vesting total, <code className="notion-code">withdrawSurplus</code> (not <code className="notion-code">withdraw</code>).
            Unsure whether your contract was transferred from L1? Look it up in the helper: the subgraph shows delegations marked “transferred to L2”.
          </div>
        </div>

        <div className="notion-steps">
          <div className="notion-step">
            <div className="notion-step-num notion-step-num--warn">1</div>
            <div className="notion-step-body">
              <h4>Bridge GRT Back to L1</h4>
              <p>Use <code className="notion-code">L2GraphTokenLockTransferTool</code> → <code className="notion-code">withdrawToL1Locked</code> to send GRT from your L2 wallet back to L1 via the Arbitrum bridge.</p>
              <div className="notion-callout notion-callout--tip">
                <span className="notion-callout-icon">⚡</span>
                <div>You can also use <a href="https://abi.ninja" target="_blank" rel="noopener noreferrer" className="notion-link">ABI Ninja</a> to call this function — just load the transfer tool contract on Arbitrum.</div>
              </div>
            </div>
          </div>
          <div className="notion-step">
            <div className="notion-step-num notion-step-num--warn">2</div>
            <div className="notion-step-body">
              <h4>Wait ~7 Days</h4>
              <p>The Arbitrum → Ethereum bridge withdrawal takes approximately <strong>7 days</strong> to finalize.</p>
            </div>
          </div>
          <div className="notion-step">
            <div className="notion-step-num notion-step-num--warn">3</div>
            <div className="notion-step-body">
              <h4>Release on Ethereum Mainnet</h4>
              <p>Call <code className="notion-code">release</code> on your original Ethereum mainnet vesting contract via <a href="https://etherscan.io" target="_blank" rel="noopener noreferrer" className="notion-link">Etherscan</a> or <a href="https://abi.ninja" target="_blank" rel="noopener noreferrer" className="notion-link">ABI Ninja</a> (chain 1).</p>
            </div>
          </div>
        </div>
      </section>

      <div className="notion-divider" />

      {/* ════════════════════════════════════════════════
          SECTION — How Vesting Works
         ════════════════════════════════════════════════ */}
      <section id="section-basics" className="notion-section">
        <button className={`notion-toggle ${openSections.basics ? 'open' : ''}`} onClick={() => toggle('basics')}>
          <span className="notion-toggle-icon" />
          <span className="notion-toggle-text">📘 How Graph Token Lock Contracts Work</span>
        </button>

        <div className={`notion-toggle-body ${openSections.basics ? 'open' : ''}`}>
          <p className="notion-text">
            Graph token lock contracts control how GRT tokens unlock over time — ensuring a structured release rather than giving all tokens at once.
          </p>

          <div className="notion-columns">
            <div className="notion-column-card">
              <h4>📅 Vesting Types</h4>
              <ul className="notion-list">
                <li><strong>Fixed Duration</strong> — 1, 2, or 4 year schedules</li>
                <li><strong>Custom Periods</strong> — monthly or custom unlocks</li>
                <li><strong>With / Without Cliff</strong> — optional waiting period before first unlock</li>
              </ul>
            </div>
            <div className="notion-column-card">
              <h4>⏳ How Tokens Unlock</h4>
              <ul className="notion-list">
                <li><strong>Linear</strong> — tokens unlock gradually over time</li>
                <li><strong>Cliff</strong> — locked, then released in chunks</li>
                <li>Tokens must be <strong>manually withdrawn</strong> once available</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── Key Elements ── */}
      <section id="section-elements" className="notion-section">
        <button className={`notion-toggle ${openSections.elements ? 'open' : ''}`} onClick={() => toggle('elements')}>
          <span className="notion-toggle-icon" />
          <span className="notion-toggle-text">🧩 Key Elements in Every Vesting Contract</span>
        </button>

        <div className={`notion-toggle-body ${openSections.elements ? 'open' : ''}`}>
          <div className="notion-table-wrap">
            <table className="notion-table">
              <thead>
                <tr><th>Property</th><th>Description</th></tr>
              </thead>
              <tbody>
                <tr><td><code>startTime</code></td><td>When vesting begins</td></tr>
                <tr><td><code>endTime</code></td><td>When all tokens are fully unlocked</td></tr>
                <tr><td><code>periods</code></td><td>How often tokens unlock (e.g., monthly)</td></tr>
                <tr><td><code>managedAmount</code></td><td>Total GRT locked in the contract</td></tr>
                <tr><td><code>tokensReleased</code></td><td>Amount already vested</td></tr>
                <tr><td><code>tokensWithdrawn</code></td><td>Amount that has been claimed</td></tr>
                <tr><td><code>vestingCliff</code></td><td>Waiting period before any tokens release</td></tr>
                <tr><td><code>releaseStartTime</code></td><td>Some contracts delay the first unlock</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── Example Scenarios ── */}
      <section id="section-examples" className="notion-section">
        <button className={`notion-toggle ${openSections.examples ? 'open' : ''}`} onClick={() => toggle('examples')}>
          <span className="notion-toggle-icon" />
          <span className="notion-toggle-text">💡 Example Scenarios</span>
        </button>

        <div className={`notion-toggle-body ${openSections.examples ? 'open' : ''}`}>
          <div className="notion-scenario-grid">
            <div className="notion-scenario">
              <div className="notion-scenario-icon">📅</div>
              <h4>4-Year Vesting, No Cliff</h4>
              <p>100,000 GRT locked for 4 years. Unlocks monthly (~2,083 GRT/month). Withdraw as tokens become available.</p>
            </div>
            <div className="notion-scenario">
              <div className="notion-scenario-icon">🧊</div>
              <h4>2-Year with 6-Month Cliff</h4>
              <p>50,000 GRT locked for 2 years. No tokens for first 6 months. After cliff, unlocks monthly.</p>
            </div>
            <div className="notion-scenario">
              <div className="notion-scenario-icon">⏳</div>
              <h4>Custom Delayed Release</h4>
              <p>125,000 GRT with no tokens unlocking until a future date. After that, starts unlocking monthly.</p>
            </div>
          </div>
        </div>
      </section>

      <div className="notion-divider" />

      {/* ── Footer ── */}
      <div className="notion-footer">
        <p>Built with 💜 by <a href="https://x.com/graphtronauts" target="_blank" rel="noopener noreferrer" className="notion-link">Graphtronauts</a></p>
        <p className="notion-footer-sub">
          Contract interaction powered by <a href="https://abi.ninja" target="_blank" rel="noopener noreferrer" className="notion-link">ABI Ninja</a> — an open-source tool by <a href="https://buidlguidl.com" target="_blank" rel="noopener noreferrer" className="notion-link">BuidlGuidl</a>
        </p>
      </div>
    </div>
  );
}

export default VestingInfo;
